/**
 * Phase 32 — the topics a channel receives, as small toggle chips: a picked
 * one is tinted with a check, a free one outlined; they appear one after
 * the other and each flip animates. The hover says which decisions the
 * topic carries.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Check } from 'lucide-react';
import { DROP_GEN_NOTIFICATION_TOPICS, DROP_GEN_TOPIC_WORDS, type DropGenNotificationTopic } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

const CHIP =
  'inline-flex h-7 items-center gap-1 whitespace-nowrap rounded-md border px-2 text-[11.5px] transition-all duration-200 active:scale-95 disabled:opacity-50 fill-mode-backwards animate-in fade-in-0 zoom-in-95 motion-reduce:animate-none';
const ON = 'border-primary/50 bg-primary/10 font-medium text-primary';
const OFF = 'border-input text-muted-foreground hover:border-primary/40 hover:text-foreground';

export const TopicChecklist: FC<{
  topics: DropGenNotificationTopic[];
  onToggle: (topic: DropGenNotificationTopic) => void;
  disabled?: boolean;
}> = ({ topics, onToggle, disabled = false }) => (
  <div role="group" aria-label="Topics" className="flex flex-wrap gap-1.5">
    {DROP_GEN_NOTIFICATION_TOPICS.map((topic, i) => {
      const on = topics.includes(topic);
      return (
        <button
          key={topic}
          type="button"
          aria-pressed={on}
          disabled={disabled}
          title={DROP_GEN_TOPIC_WORDS[topic].hint}
          onClick={() => onToggle(topic)}
          style={{ animationDelay: `${i * 40}ms` }}
          className={cn(CHIP, on ? ON : OFF)}
        >
          <Check className={cn('h-3 w-3 transition-all duration-200', on ? 'opacity-100' : 'w-0 opacity-0')} aria-hidden />
          {DROP_GEN_TOPIC_WORDS[topic].label}
        </button>
      );
    })}
  </div>
);
